/**
 * Owner-Session (Sprint A.5, Feb 2026)
 *
 * AUTH-SICHERHEIT V2 · Phase 2 (Feb 2026, betreibergenehmigt): Der Auth-Token
 * wandert vom localStorage in ein httpOnly-Cookie (`owner_session_cookie`,
 * gesetzt vom Backend-Login-Endpoint). Dieser Hook speichert nur noch
 * NICHT-geheime Metadaten (company_name, company_code, tenant_id,
 * must_change_pin) im localStorage. XSS kann dem Nutzer keine Session mehr
 * stehlen.
 *
 * Sonderfall: Der anonyme LP3-Vorschau-Fluss (BLOCK 3D · R2) legt eine
 * `owner_session` mit `tenant_id: "public-preview"` und einem
 * NICHT-geheimen `preview_token` an — dieses Token wird ausschließlich für
 * Preview-Sessions als Bearer-Header angefügt (siehe
 * `useEmployeeSession.jsx`-Interceptor). Reguläre Owner-Sessions haben KEIN
 * Token-Feld und keinen preview_token.
 *
 * Speicher-Vertrag (regulärer Owner):
 *   localStorage["owner_session"] = {
 *     tenant_id, company_name, company_code, must_change_pin  // KEIN token
 *   }
 * Speicher-Vertrag (Preview-Session, unverändert):
 *   localStorage["owner_session"] = { preview_token, tenant_id: "public-preview" }
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api, client } from "@/lib/api";
import { vergissVorschau } from "@/lib/generierungsbegriff";

const STORAGE_KEY = "owner_session";
const LAST_CODE_KEY = "last_company_code";

export function getRememberedCompanyCode() {
  try {
    return localStorage.getItem(LAST_CODE_KEY) || "";
  } catch {
    return "";
  }
}

function readSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeSession(s) {
  if (!s) {
    localStorage.removeItem(STORAGE_KEY);
    return;
  }
  // AUTH-SICHERHEIT V2 · Phase 2: reguläre Owner-Sessions dürfen KEIN
  // token-Feld tragen. Falls Aufrufer versehentlich token/preview_token
  // mitgeben und die tenant_id NICHT "public-preview" ist, wird das
  // Token-Feld deterministisch verworfen — Single-Point-of-Truth gegen
  // Rückfälle. Preview-Sessions behalten ihren `preview_token` als
  // öffentliches Bearer-Marker (kein XSS-Risiko: preview-Token ist per
  // Design nicht geheim und nur course-scoped gültig).
  const isPreview = s.tenant_id === "public-preview";
  const cleaned = isPreview
    ? {
        preview_token: typeof s.preview_token === "string" ? s.preview_token : "",
        tenant_id: "public-preview",
      }
    : {
        tenant_id: s.tenant_id ?? "",
        company_name: s.company_name ?? "",
        company_code: s.company_code ?? "",
        must_change_pin: !!s.must_change_pin,
      };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(cleaned));
}

/**
 * AUTH-SICHERHEIT V2 · Phase 4b (Finale): Der deprecated Export
 * `getStoredOwnerToken` (Phase-2-Übergang, lieferte konstant `null`) ist
 * endgültig entfernt. Der Owner-Token existiert clientseitig NICHT mehr —
 * er lebt ausschließlich im httpOnly-Cookie. Für den anonymen Preview-Fluss
 * existiert ein separater Getter im useEmployeeSession-Interceptor
 * (`readPreviewOwnerToken`).
 */
const OwnerSessionContext = createContext({
  session: null,
  loading: true,
  login: async () => {},
  logout: async () => {},
  refresh: async () => {},
  setMustChangePin: () => {},
});

export function OwnerSessionProvider({ children }) {
  const [session, setSession] = useState(readSession);
  const [loading, setLoading] = useState(true);

  // Bootstrap: gegen /auth/owner/me prüfen, ob die Cookie-Session noch lebt.
  // Wenn lokal keine Metadaten vorliegen (frischer Besucher), ersparen wir
  // uns den 401-Roundtrip. Preview-Sessions gehen ebenfalls durch
  // /auth/owner/me — der Interceptor hängt den preview_token als Bearer an.
  useEffect(() => {
    const stored = readSession();
    if (!stored) {
      Promise.resolve().then(() => setLoading(false));
      return;
    }
    let cancelled = false;
    api.getOwnerMe()
      .then((data) => {
        if (cancelled) return;
        const isPreview = stored.tenant_id === "public-preview";
        const next = isPreview
          ? { preview_token: stored.preview_token || "", tenant_id: "public-preview" }
          : {
              tenant_id: data.tenant_id,
              company_name: data.company_name,
              company_code: data.company_code,
              must_change_pin: !!data.must_change_pin,
            };
        writeSession(next);
        setSession(next);
      })
      .catch(() => {
        if (cancelled) return;
        writeSession(null);
        setSession(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Bfcache-Restore-Schutz (Feb 2026, betreibergenehmigt nach Forensik-Report
  // FORENSIK_BFCACHE_SESSION_LECK): Wenn der Browser diese Seite aus dem
  // Back-Forward-Cache wiederherstellt (Zurück-Button nach Login-Wechsel),
  // ist der obere useEffect-Bootstrap NICHT erneut aktiv (kein Re-Mount).
  // Ohne diesen Handler würde die veraltete React-State-Kopie der VORHERIGEN
  // Owner-Session gerendert. `event.persisted === true` markiert den
  // bfcache-Restore — wir erzwingen einen vollen Reload, damit die
  // aktuell im Cookie-Store stehende Session frisch validiert wird
  // (getOwnerMe() läuft dann regulär im Mount-Effekt).
  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        window.location.reload();
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  // ==========================================================================
  // IDLE-AUTO-LOGOUT (Feb 2026, betreibergenehmigt)
  //
  // Verhalten (siehe Auftrag CLARTEXT · IDLE-AUTO-LOGOUT):
  //   - Aktivitäts-Events (mousemove-debounced, keydown, click, scroll,
  //     touchstart) setzen den lokalen Idle-Timer zurück.
  //   - 60 s vor Ablauf erscheint ein modaler Warndialog mit Countdown und
  //     "Weiterarbeiten"-Knopf. Der Dialog überlagert Formulare, ohne sie
  //     auszutauschen — Werte bleiben nach "Weiterarbeiten" erhalten.
  //   - Vor dem finalen Logout wird `syncIdleTimerWithServer()` aufgerufen
  //     (Pre-Logout-Server-Check, Multi-Tab-Schutz). Nur wenn der Server
  //     bestätigt, dass residual ≤ 0 ist, findet der Logout tatsächlich statt.
  //   - `visibilitychange` (Tab wieder sichtbar) resynchronisiert einmal
  //     gegen den Server — behebt Browser-Timer-Drosselung in Hintergrund-
  //     Tabs.
  //   - Alles ist rein additiv zum bestehenden pageshow-Handler (Bfcache-Fix,
  //     eingefroren) und zum Bootstrap-Effect oben.
  //
  // Sperrzonen: KEINE Berührung an `useEmployeeSession.jsx`, `auth_employee.py`,
  // `owner_logout`-Endpoint, `owner_sessions.{token, tenant_id, revoked,
  // created_at}`, oder am pageshow-Block darüber.
  // ==========================================================================

  const WARN_LEAD_SECONDS = 60;
  const [idleWarningOpen, setIdleWarningOpen] = useState(false);
  const [idleCountdown, setIdleCountdown] = useState(WARN_LEAD_SECONDS);
  const idleTimeoutMinutesRef = useRef(30);
  const warnTimerRef = useRef(null);
  const finalTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const lastServerActivityRef = useRef(null); // ISO-String vom Server

  const clearAllIdleTimers = useCallback(() => {
    if (warnTimerRef.current) { clearTimeout(warnTimerRef.current); warnTimerRef.current = null; }
    if (finalTimerRef.current) { clearTimeout(finalTimerRef.current); finalTimerRef.current = null; }
    if (countdownTimerRef.current) { clearInterval(countdownTimerRef.current); countdownTimerRef.current = null; }
  }, []);

  const performIdleLogout = useCallback(async () => {
    clearAllIdleTimers();
    // Fix B (F5-Dialog-Bug, Feb 2026): Warndialog strukturell konsistent
    // schließen — zusätzlich zu setIdleWarningOpen(false) auch den
    // Countdown auf 0 setzen, damit die Rendering-Bedingung
    // (idleWarningOpen && idleCountdown > 0) unabhängig von React-Batching
    // in derselben Tick-Grenze konsistent false ergibt.
    setIdleWarningOpen(false);
    setIdleCountdown(0);
    try {
      await api.ownerLogout();
    } catch { /* fail-safe: lokal trotzdem aufräumen */ }
    writeSession(null);
    setSession(null);
    if (typeof window !== "undefined") {
      window.location.href = "/unternehmer/login?reason=idle";
    }
  }, [clearAllIdleTimers]);

  // Kernprimitive: startet/erneuert die zwei Timer basierend auf einem
  // "gültig bis"-Zeitpunkt in Sekunden (relativ zu jetzt).
  const armIdleTimers = useCallback((residualSeconds) => {
    clearAllIdleTimers();
    // Wenn residual < 0 → nichts, der Aufrufer entscheidet über Logout.
    if (residualSeconds <= 0) return;
    // Warnung 60 s vor Ablauf, oder sofort wenn residual < 60 s.
    const msToWarn = Math.max(0, (residualSeconds - WARN_LEAD_SECONDS) * 1000);
    const msToFinal = residualSeconds * 1000;
    warnTimerRef.current = setTimeout(() => {
      const startCountdown = Math.min(WARN_LEAD_SECONDS, residualSeconds);
      setIdleCountdown(startCountdown);
      setIdleWarningOpen(true);
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = setInterval(() => {
        setIdleCountdown((c) => (c > 0 ? c - 1 : 0));
      }, 1000);
    }, msToWarn);
    finalTimerRef.current = setTimeout(() => {
      // Pre-Logout-Server-Check gleich hier: nicht direkt logout, sondern
      // syncIdleTimerWithServer aufrufen — die Sync-Funktion entscheidet
      // final. Ref-basierter Zugriff, damit spätere Neubindung greift.
      syncIdleTimerWithServerRef.current?.();
    }, msToFinal);
  }, [clearAllIdleTimers]);

  // Ref-Trick: syncIdleTimerWithServer und armIdleTimers referenzieren sich
  // gegenseitig. Ref-Basierter Zugriff bricht die Bindungsschleife.
  const syncIdleTimerWithServerRef = useRef(null);

  const syncIdleTimerWithServer = useCallback(async () => {
    try {
      const data = await api.getOwnerMe();
      // Feld-Fallbacks — Backend liefert seit dem Idle-Fix
      // last_activity_at + idle_timeout_minutes; Bestandsclient sieht evtl.
      // ältere Backend-Version und muss defensiv laufen.
      const idleMin = Number.isFinite(data?.idle_timeout_minutes)
        ? data.idle_timeout_minutes
        : 30;
      idleTimeoutMinutesRef.current = Math.min(240, Math.max(5, idleMin));
      const lastIso = data?.last_activity_at || null;
      lastServerActivityRef.current = lastIso;
      let residual;
      if (lastIso) {
        const lastMs = Date.parse(lastIso);
        residual = Math.floor((lastMs + idleTimeoutMinutesRef.current * 60000 - Date.now()) / 1000);
      } else {
        residual = idleTimeoutMinutesRef.current * 60;
      }
      if (residual <= 0) {
        // Server sagt: wirklich abgelaufen. Logout ausführen.
        await performIdleLogout();
        return;
      }
      // Warndialog schließen, falls fälschlich offen (Multi-Tab-Szenario:
      // andere Tabs haben Aktivität eingespeist, Server ist frisch).
      // Fix B (F5-Dialog-Bug): Countdown zusätzlich auf 0, damit die
      // gerenderte Bedingung (idleWarningOpen && idleCountdown > 0)
      // deterministisch false ist — unabhängig vom setInterval-Tick, der
      // sonst den zuletzt gesehenen Countdown-Wert nachziehen könnte.
      setIdleWarningOpen(false);
      setIdleCountdown(0);
      armIdleTimers(residual);
    } catch (e) {
      // 401 vom Server → der 401-Interceptor (weiter unten) räumt bereits
      // auf und redirected. Andere Fehler (Netzwerk) → lokalen Timer
      // konservativ auf Default weiterlaufen lassen.
      if (e?.response?.status !== 401) {
        armIdleTimers(idleTimeoutMinutesRef.current * 60);
      }
    }
  }, [armIdleTimers, performIdleLogout]);

  useEffect(() => {
    syncIdleTimerWithServerRef.current = syncIdleTimerWithServer;
  }, [syncIdleTimerWithServer]);

  // Idle-Bootstrap: sobald eine reguläre Owner-Session vorliegt (keine
  // Preview-Session, keine Loading-Phase), einmal syncIdleTimerWithServer
  // aufrufen und danach die Aktivitäts-/Visibility-Listener aktivieren.
  useEffect(() => {
    if (loading) return;
    const isRegularOwner = !!session && session.tenant_id !== "public-preview";
    if (!isRegularOwner) {
      clearAllIdleTimers();
      setIdleWarningOpen(false);
      return () => {};
    }

    // Erst-Sync — der Server liefert last_activity_at + idle_timeout_minutes.
    syncIdleTimerWithServer();

    // Debouncer für mousemove — der Event feuert bei aktiver Maus hunderte
    // Mal pro Sekunde. Nur alle 500 ms den Timer neu armen; sonst wären
    // die setTimeout-Zyklen selbst der Bottleneck.
    let mouseMoveDebounce = null;
    const resetOnActivity = () => {
      // Lokaler Timer-Reset ohne Server-Roundtrip. Der Server bekommt
      // Aktivität ohnehin durch die normalen API-Requests (die durch
      // Nutzeraktion ausgelöst werden) — `require_owner` schreibt
      // last_activity_at gedrosselt (Delta > 60 s).
      armIdleTimers(idleTimeoutMinutesRef.current * 60);
      // Warndialog schließen falls offen. Wir rufen setIdleWarningOpen(false)
      // unconditionally — setState mit gleichem Wert ist ein React-No-Op.
      // Die closure würde sonst den `idleWarningOpen`-Wert vom Zeitpunkt der
      // Effect-Registrierung sehen (immer false) und nie schließen.
      // Fix B (F5-Dialog-Bug): Countdown zusätzlich auf 0 setzen, damit die
      // Rendering-Bedingung strukturell konsistent bleibt.
      setIdleWarningOpen(false);
      setIdleCountdown(0);
    };
    const onMouseMove = () => {
      if (mouseMoveDebounce) return;
      mouseMoveDebounce = setTimeout(() => {
        mouseMoveDebounce = null;
        resetOnActivity();
      }, 500);
    };
    const onOther = () => resetOnActivity();
    const onVisibility = () => {
      if (typeof document !== "undefined" && document.hidden === false) {
        // Tab kommt in Vordergrund — Timer-Drosselung möglich, Server-Sync.
        syncIdleTimerWithServer();
      }
    };
    window.addEventListener("mousemove", onMouseMove, { passive: true });
    window.addEventListener("keydown", onOther, { passive: true });
    window.addEventListener("click", onOther, { passive: true });
    window.addEventListener("scroll", onOther, { passive: true });
    window.addEventListener("touchstart", onOther, { passive: true });
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      if (mouseMoveDebounce) clearTimeout(mouseMoveDebounce);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("keydown", onOther);
      window.removeEventListener("click", onOther);
      window.removeEventListener("scroll", onOther);
      window.removeEventListener("touchstart", onOther);
      document.removeEventListener("visibilitychange", onVisibility);
      clearAllIdleTimers();
    };
    // idleWarningOpen absichtlich NICHT im Dep-Array — sonst re-mount jeden
    // Tick und die Timer resetten sich selbst. Wir schließen den Dialog
    // impertativ via setIdleWarningOpen.
  }, [session, loading, armIdleTimers, clearAllIdleTimers, syncIdleTimerWithServer]);

  const continueWorking = useCallback(() => {
    // "Weiterarbeiten"-Klick: Server-Sync auslösen — der GET /auth/owner/me
    // aktualisiert selbst last_activity_at (via require_owner-Drossel),
    // liefert das neue idle_timeout_minutes und wir armen neu.
    // Fix B (F5-Dialog-Bug): Countdown zusätzlich auf 0 (siehe Kommentar
    // an syncIdleTimerWithServer).
    setIdleWarningOpen(false);
    setIdleCountdown(0);
    syncIdleTimerWithServer();
  }, [syncIdleTimerWithServer]);

  const login = useCallback(async (companyCode, pin) => {
    const res = await api.ownerLogin(companyCode, pin);
    // Backend setzt in derselben Response das httpOnly-Cookie; das
    // `token`-Feld im Body wird bewusst ignoriert (Phase-4-Aufgabe: dort
    // wird es serverseitig entfernt).
    const next = {
      tenant_id: res.tenant_id,
      company_name: res.company_name,
      company_code: res.company_code,
      must_change_pin: !!res.must_change_pin,
    };
    writeSession(next);
    setSession(next);
    try {
      if (res.company_code) localStorage.setItem(LAST_CODE_KEY, res.company_code);
    } catch {
      /* localStorage optional */
    }
    return next;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.ownerLogout();
    } catch {
      /* lokal trotzdem aufräumen */
    }
    writeSession(null);
    setSession(null);
  }, []);

  const refresh = useCallback(async () => {
    const stored = readSession();
    if (!stored) return null;
    try {
      const data = await api.getOwnerMe();
      const isPreview = stored.tenant_id === "public-preview";
      const next = isPreview
        ? { preview_token: stored.preview_token || "", tenant_id: "public-preview" }
        : {
            tenant_id: data.tenant_id,
            company_name: data.company_name,
            company_code: data.company_code,
            must_change_pin: !!data.must_change_pin,
          };
      writeSession(next);
      setSession(next);
      return next;
    } catch {
      writeSession(null);
      setSession(null);
      return null;
    }
  }, []);

  // Wird vom PIN-Change-Flow gerufen, damit das Banner sofort verschwindet
  // (ohne erst /owner/me erneut abfragen zu müssen).
  const setMustChangePin = useCallback((value) => {
    setSession((s) => (s ? { ...s, must_change_pin: !!value } : s));
    const stored = readSession();
    if (stored) writeSession({ ...stored, must_change_pin: !!value });
  }, []);

  return (
    <OwnerSessionContext.Provider
      value={{ session, loading, login, logout, refresh, setMustChangePin }}
    >
      {children}
      {/* IDLE-AUTO-LOGOUT (Feb 2026): Warndialog — additiv, modal, überlagert
          Formulare ohne sie auszutauschen. Werte im Formular bleiben nach
          "Weiterarbeiten" erhalten.
          Fix B (F5-Dialog-Bug, Feb 2026): Rendering-Bedingung strukturell
          verschärft auf `idleWarningOpen && idleCountdown > 0`. Damit ist
          der Dialog auch dann sicher unsichtbar, wenn ein noch nicht
          durchgeflushter setInterval-Tick den Countdown-Wert kurzzeitig >0
          hält, aber `setIdleWarningOpen(false)` + `setIdleCountdown(0)`
          bereits gerufen wurden — jede Ende-Route (syncIdleTimer,
          resetOnActivity, continueWorking, performIdleLogout) setzt beide
          Zustände gemeinsam. */}
      {idleWarningOpen && idleCountdown > 0 && (
        <div
          data-testid="idle-warning-overlay"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.55)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
        >
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="idle-warning-title"
            data-testid="idle-warning-dialog"
            style={{
              background: "white",
              borderRadius: 8,
              maxWidth: 440,
              width: "100%",
              padding: 24,
              boxShadow: "0 20px 40px rgba(0,0,0,0.25)",
            }}
          >
            <h2
              id="idle-warning-title"
              style={{ margin: 0, fontSize: 18, fontWeight: 600, color: "#0f172a" }}
            >
              Sie werden gleich abgemeldet
            </h2>
            <p style={{ color: "#334155", fontSize: 14, marginTop: 12 }}>
              Aus Sicherheitsgründen werden Sie in{" "}
              <strong data-testid="idle-warning-countdown">{idleCountdown}</strong>{" "}
              Sekunden wegen Inaktivität abgemeldet. Klicken Sie auf
              {" \u201EWeiterarbeiten\u201C, "}um Ihre Sitzung fortzusetzen — Formulareingaben
              bleiben erhalten.
            </p>
            <div
              style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}
            >
              <button
                type="button"
                onClick={performIdleLogout}
                data-testid="idle-warning-logout-now"
                style={{
                  background: "transparent",
                  border: "1px solid #cbd5e1",
                  borderRadius: 6,
                  padding: "8px 14px",
                  color: "#475569",
                  cursor: "pointer",
                  fontSize: 14,
                }}
              >
                Jetzt abmelden
              </button>
              <button
                type="button"
                onClick={continueWorking}
                data-testid="idle-warning-continue"
                style={{
                  background: "#0f172a",
                  color: "white",
                  border: "none",
                  borderRadius: 6,
                  padding: "8px 14px",
                  cursor: "pointer",
                  fontSize: 14,
                  fontWeight: 500,
                }}
              >
                Weiterarbeiten
              </button>
            </div>
          </div>
        </div>
      )}
    </OwnerSessionContext.Provider>
  );
}

// IDLE-AUTO-LOGOUT (Feb 2026, betreibergenehmigt) — 401-Interceptor für Owner-
// Routen. Analog zum bestehenden Muster in `useEmployeeSession.jsx` (nicht
// verändert, Sperrzone), aber als NEUER, eigenständiger Interceptor. Deckt
// sowohl den Idle-Timeout-Fall als auch andere 401-Ursachen (serverseitig
// widerrufene Cookies etc.). Redirect trägt `?reason=idle` — die Login-Seite
// zeigt bei diesem Reason einen klaren Hinweis-Toast.
function _isOwnerRoute(url) {
  if (!url) return false;
  // Employee-Routen ausdrücklich AUSKLAMMERN — der Employee-Interceptor in
  // useEmployeeSession.jsx behandelt sie.
  if (url.startsWith("/me/") || url === "/me") return false;
  if (url === "/auth/me" || url === "/auth/employee-logout" || url.startsWith("/auth/employee-")) return false;
  // Öffentliche Routen sind KEINE Owner-Routen — kein Redirect.
  if (url.startsWith("/health") || url.startsWith("/catalog")) return false;
  if (url.startsWith("/lp/") || url.startsWith("/world-preview") || url.startsWith("/preview-images")) return false;
  if (url.startsWith("/suggestions") || url.startsWith("/plakat")) return false;
  if (url.startsWith("/billing/webhook") || url.startsWith("/billing/one-time")) return false;
  if (url === "/auth/owner/has-default-pin" || url.startsWith("/auth/owner/has-default-pin")) return false;
  // Der Login-Endpoint selbst darf keinen Redirect auslösen — sonst
  // schluckt der Interceptor den Login-Fehler und redirected auf sich selbst.
  if (url === "/auth/owner-login") return false;
  return true;
}

// ADS-AUTOPREVIEW 01 · H2-A · U1: Einen Bearer sendet ausschließlich die öffentliche Vorschau (useEmployeeSession.jsx).
function _abgelehntesVorschauToken(config) {
  const h = config?.headers;
  const wert = (typeof h?.get === "function" ? h.get("Authorization") : h?.Authorization) || "";
  return typeof wert === "string" && wert.startsWith("Bearer ") ? wert.slice(7) : "";
}

// Nur die im Tab gemerkte Vorschau mit genau diesem abgelehnten Token vergessen (sonst Wiederöffnen der toten Welt).
function _gemerkteVorschauMitTokenVergessen(token) {
  try {
    const gemerkt = JSON.parse(window.sessionStorage.getItem("clartext_lp3_vorschau") || "null");
    if (gemerkt?.preview_session_token === token) vergissVorschau();
  } catch { /* sessionStorage optional */ }
}

client.interceptors.response.use(
  (r) => r,
  (error) => {
    if (typeof window === "undefined") return Promise.reject(error);
    const status = error?.response?.status;
    if (status !== 401) return Promise.reject(error);
    const url = error?.config?.url || "";
    if (!_isOwnerRoute(url)) return Promise.reject(error);
    // Lokal aufräumen — Cookie hat der Server bei einem 401 (Idle-Timeout)
    // ohnehin schon revoked; wir spiegeln nur den Client-State.
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch { /* localStorage optional */ }
    // Nur weiterleiten, wenn wir nicht bereits auf der Login-Seite sind
    // (verhindert Endlos-Redirect-Schleifen bei fehlgeschlagenen /me-Calls
    // während der Login-Seite geladen wird).
    const path = window.location.pathname || "";
    const vorschauToken = _abgelehntesVorschauToken(error?.config);
    if (vorschauToken) {
      // H2-A · U1: abgelaufene öffentliche Vorschau – nie zur Unternehmer-Anmeldung.
      _gemerkteVorschauMitTokenVergessen(vorschauToken);
      if (path.startsWith("/unternehmer/welt/")) {
        window.location.replace("/");
        return new Promise(() => {}); // keine weitere Login-Weiterleitung während der Navigation
      }
      if (!path.startsWith("/unternehmer")) return Promise.reject(error);
    }
    if (!path.startsWith("/unternehmer/login")) {
      window.location.href = "/unternehmer/login?reason=idle";
    }
    return Promise.reject(error);
  },
);

export function useOwnerSession() {
  return useContext(OwnerSessionContext);
}
